// normalized, as advised in https://redux.js.org/usage/structuring-reducers/normalizing-state-shape

// Public user profiles (name + avatar, GET /me/users/{userId}) — kept apart
// from usersSlice, whose entities are the full admin-only User records.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "../api/api";
import { logOut } from "./globalSlice";
import { addEntityFetchReducers, EntityState } from "./entity";
import { UserProfile } from "../api/models";
import { RootState } from "./store";
import { useFetchableState } from "./fetchableState";

type ProfilesState = EntityState<UserProfile>

const initialState: ProfilesState = {
    entities: {}
}

export const profilesSlice = createSlice({
    name: 'profiles',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            UserProfile,
            { user: UserProfile },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.user,
            fetchProfileThunk
        )
        builder.addCase(logOut, (state: ProfilesState) => {
            state = initialState;
        })
    }
})

const fetchProfileThunk = createAsyncThunk(
    'profiles/fetchProfile',
    async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.profiles.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getUserProfile({ userId: id, accessToken });
    }
)

export function useUserProfile(userId: string, accessToken: string) {
    return useFetchableState<UserProfile>({
        selector: (state: RootState) => state.profiles.entities[userId],
        action: fetchProfileThunk({ id: userId, accessToken })
    })
}

export default profilesSlice.reducer
